import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { messageI18n } from '../../../helpers/i18n/toastI18n';
import {
  User,
  Mail,
  Phone,
  Calendar,
  Database,
  Lock,
  CheckCircle,
  Clock,
  Settings,
  Activity,
  ShieldCheck,
  Fingerprint,
  ShieldAlert,
  KeyRound,
} from 'lucide-react';

import {
  useAdminSetTemporaryPassword,
  useSingleUser,
} from '../../../helpers/api/users/users';
import {
  useInitiatePasswordChange,
  useConfirmPasswordChange,
} from '../../../helpers/api/auth/auth';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';

import { FormField } from '../../ComponnentsUI/common/FormField';
import Preferences from './newPreferences';
import PasswordInput from '../../ComponnentsUI/common/PasswordInput';
import TwoFactorAuthSettings from './TwoFactorAuthSettings';
import OwnershipTransferSettings from './OwnershipTransferSettings';
import { useMe } from '../../../helpers/api/users/users';
import { usePermissions } from '../../../helpers/acl/permissions';
import { extractPasswordValidationError } from '../../../helpers/passwordValidation';
import {
  translatePasswordError,
  validatePasswordStrengthI18n,
} from '../../../helpers/i18n/passwordValidationI18n';

const InfoPair = ({ label, value, icon: Icon, notProvidedText }) => (
  <div className="flex flex-col py-2 border-b border-gray-50 dark:border-gray-800 last:border-0">
    <div className="flex items-center gap-2 mb-1">
      {Icon && (
        <Icon size={12} className="text-blue-500/70 dark:text-blue-400/70" />
      )}
      <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">
        {label}
      </span>
    </div>
    <span className="text-sm font-bold text-slate-700 dark:text-slate-200 px-1">
      {value || (
        <span className="text-gray-300 dark:text-gray-600 font-normal italic text-xs">
          {notProvidedText}
        </span>
      )}
    </span>
  </div>
);

const ProfilePage = () => {
  const { t } = useTranslation('AccountPage');
  const { t: tPref } = useTranslation('UserPreferencesModal');
  const { uid } = useParams();
  const [showVerify, setShowVerify] = useState(false);
  const [passwordFields, setPasswordFields] = useState({
    new_password: '',
    confirm_password: '',
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const { data: profileData, isLoading } = useSingleUser(uid);
  const user = profileData?.data;
  const { data: me, isLoading: isMeLoading } = useMe();
  const { can } = usePermissions(); // must run every render (before any early return)

  const initiatePasswordChange = useInitiatePasswordChange(
    undefined,
    undefined,
    false
  );
  const confirmPasswordChange = useConfirmPasswordChange();
  const setTemporaryPassword = useAdminSetTemporaryPassword();

  useDocumentTitle(
    user
      ? t('documentTitle', { name: `${user.first_name} ${user.last_name}` })
      : t('profileTitle')
  );

  const handleInitiateSubmit = (e) => {
    e.preventDefault();
    if (!passwordFields.new_password || !passwordFields.confirm_password) {
      setFormErrors({
        new_password: !passwordFields.new_password ? t('required') : '',
        confirm_password: !passwordFields.confirm_password ? t('required') : '',
      });
      return;
    }

    const passwordError = validatePasswordStrengthI18n(passwordFields.new_password);
    if (passwordError) {
      setFormErrors({ new_password: passwordError });
      return;
    }

    if (passwordFields.new_password !== passwordFields.confirm_password) {
      setFormErrors({ confirm_password: t('passwordsDoNotMatch') });
      return;
    }

    initiatePasswordChange.mutate(
      { new_password: passwordFields.new_password },
      {
        onSuccess: () => {
          setShowVerify(true);
          setFormErrors({});
        },
        onError: (error) => {
          const passwordError = translatePasswordError(
            extractPasswordValidationError(error)
          );
          setFormErrors(
            passwordError ? { new_password: passwordError } : {}
          );
        },
      }
    );
  };

  const handleTemporaryPasswordSubmit = (e) => {
    e.preventDefault();
    if (!passwordFields.new_password || !passwordFields.confirm_password) {
      setFormErrors({
        new_password: !passwordFields.new_password ? t('required') : '',
        confirm_password: !passwordFields.confirm_password ? t('required') : '',
      });
      return;
    }

    const passwordError = validatePasswordStrengthI18n(passwordFields.new_password);
    if (passwordError) {
      setFormErrors({ new_password: passwordError });
      return;
    }

    if (passwordFields.new_password !== passwordFields.confirm_password) {
      setFormErrors({ confirm_password: t('passwordsDoNotMatch') });
      return;
    }

    setTemporaryPassword.mutate(
      {
        userId: String(user.id),
        temporary_password: passwordFields.new_password,
      },
      {
        onSuccess: () => {
          messageI18n.success('account.tempPasswordSet');
          setPasswordFields({ new_password: '', confirm_password: '' });
          setFormErrors({});
        },
        onError: (error) => {
          const passwordError = translatePasswordError(
            extractPasswordValidationError(error)
          );
          setFormErrors(
            passwordError ? { new_password: passwordError } : {}
          );
        },
      }
    );
  };

  const handleConfirmSubmit = (e) => {
    e.preventDefault();
    if (!verificationCode || (user?.is_2fa_enabled && !totpCode)) {
      setFormErrors({
        verification_code: !verificationCode ? t('enterVerificationCode') : '',
        totp_code:
          user?.is_2fa_enabled && !totpCode ? t('enterTwoFactorCode') : '',
      });
      return;
    }

    confirmPasswordChange.mutate(
      {
        verification_code: verificationCode,
        ...(user?.is_2fa_enabled ? { totp_code: totpCode } : {}),
      },
      {
        onSuccess: () => {
          setShowVerify(false);
          setPasswordFields({ new_password: '', confirm_password: '' });
          setVerificationCode('');
          setTotpCode('');
        },
      }
    );
  };

  if (isLoading || isMeLoading)
    return (
      <div className="p-20 text-center font-bold text-gray-400 dark:text-gray-500 animate-pulse">
        {t('loading')}
      </div>
    );
  if (!user)
    return (
      <div className="p-20 text-center dark:text-gray-400">{t('userNotFound')}</div>
    );

  const aclRoles: string[] = (user as any)?.acl_roles || [];
  const roleLabel = (user as any)?.is_tenant_owner
    ? t('staff.owner', 'Owner')
    : aclRoles.length
      ? aclRoles.join(', ')
      : t('staff.noRole', 'No role');
  const isOwnProfile = me && user && String(me.id) === String(user.id);
  const canAdminResetPassword = can('user.manage') && !isOwnProfile;

  return (
    <div className="w-full mx-auto p-6 lg:p-8 space-y-6 bg-transparent">
      {/* --- 1. HEADER SECTION --- */}
      <header className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-md border border-slate-200 dark:border-slate-800 animate-in fade-in flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-blue-100 dark:shadow-none">
              <User size={36} strokeWidth={1.5} />
            </div>
            <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-900 p-0.5 rounded-full shadow-sm">
              <CheckCircle
                size={18}
                className="text-green-500 fill-white dark:fill-slate-900"
              />
            </div>
          </div>
          <div className="space-y-1 text-center md:text-left">
            <div className="flex flex-col md:flex-row items-center gap-3">
              <h1 className="text-2xl font-extrabold text-[#1E293B] dark:text-white tracking-tight leading-none">
                {user.first_name} {user.last_name}
              </h1>
              <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-black rounded-md px-2 py-1 text-[11px] tracking-wider uppercase border-0">
                {t('verifiedStaff')}
              </span>
            </div>
            <div className="flex gap-3 justify-center md:justify-start">
              <span className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-1">
                <Database size={12} className="text-blue-500" /> {user.ae_title}
              </span>
              <span className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-1">
                <ShieldCheck size={12} className="text-blue-500" /> {roleLabel}
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <section className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gray-50/50 dark:bg-slate-800/50 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity size={16} className="text-blue-600" />
                <h2 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-tight">
                  {t('userInformation')}
                </h2>
              </div>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-[1.6rem]">
              <InfoPair
                label={t('firstName')}
                value={user.first_name}
                icon={User}
                notProvidedText={t('notProvided')}
              />
              <InfoPair
                label={t('lastName')}
                value={user.last_name}
                icon={User}
                notProvidedText={t('notProvided')}
              />
              <InfoPair
                label={t('clinicalEmail')}
                value={user.email}
                icon={Mail}
                notProvidedText={t('notProvided')}
              />
              <InfoPair
                label={t('contact')}
                value={user.phone_number}
                icon={Phone}
                notProvidedText={t('notProvided')}
              />
              <InfoPair
                label={t('birthday')}
                value={user.date_of_birth}
                icon={Calendar}
                notProvidedText={t('notProvided')}
              />
              <InfoPair
                label={t('aeTitle')}
                value={user.ae_title}
                icon={Database}
                notProvidedText={t('notProvided')}
              />
            </div>
          </section>

          <section className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gray-50/50 dark:bg-slate-800/50 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings size={16} className="text-indigo-600" />
                <h2 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-tight">
                  {tPref('sectionTitle')}
                </h2>
              </div>
            </div>
            <div className="p-6">
              <Preferences />
            </div>
          </section>
        </div>

        {/* RIGHT COLUMN */}
        <div className="lg:col-span-4 space-y-6">
          {/* Security Section */}
          <section className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gray-50/50 dark:bg-slate-800/50 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock size={16} className="text-rose-600" />
                <h2 className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-tight">
                  {canAdminResetPassword ? t('resetPassword') : t('security')}
                </h2>
              </div>
            </div>
            <div className="p-6">
              {canAdminResetPassword ? (
                <form onSubmit={handleTemporaryPasswordSubmit} className="space-y-6">
                  <FormField
                    label={t('temporaryPassword')}
                    icon={KeyRound}
                    required
                    error={formErrors.new_password}
                  >
                    <PasswordInput
                      value={passwordFields.new_password}
                      onChange={(e) =>
                        {
                          setPasswordFields({
                            ...passwordFields,
                            new_password: e.target.value,
                          });
                          if (formErrors.new_password) {
                            setFormErrors((prev) => ({
                              ...prev,
                              new_password: '',
                            }));
                          }
                        }
                      }
                      className="rounded-xl h-8 border-gray-200 dark:border-gray-700 dark:bg-slate-800 dark:text-white"
                    />
                  </FormField>
                  <FormField
                    label={t('confirmTemporaryPassword')}
                    icon={KeyRound}
                    required
                    error={formErrors.confirm_password}
                  >
                    <PasswordInput
                      value={passwordFields.confirm_password}
                      onChange={(e) =>
                        {
                          setPasswordFields({
                            ...passwordFields,
                            confirm_password: e.target.value,
                          });
                          if (formErrors.confirm_password) {
                            setFormErrors((prev) => ({
                              ...prev,
                              confirm_password: '',
                            }));
                          }
                        }
                      }
                      className="rounded-xl h-8 border-gray-200 dark:border-gray-700 dark:bg-slate-800 dark:text-white"
                    />
                  </FormField>

                  <button
                    type="submit"
                    disabled={setTemporaryPassword.isPending}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed text-white rounded-xl text-[12px] font-bold shadow-lg shadow-blue-50 dark:shadow-none transition-colors"
                  >
                    {setTemporaryPassword.isPending
                      ? t('settingPassword')
                      : t('setTemporaryPassword')}
                  </button>
                </form>
              ) : !showVerify ? (
                <form onSubmit={handleInitiateSubmit} className="space-y-6">
                  <FormField
                    label={t('newPassword')}
                    icon={KeyRound}
                    required
                    error={formErrors.new_password}
                  >
                    <PasswordInput
                      value={passwordFields.new_password}
                      onChange={(e) =>
                        {
                          setPasswordFields({
                            ...passwordFields,
                            new_password: e.target.value,
                          });
                          if (formErrors.new_password) {
                            setFormErrors((prev) => ({
                              ...prev,
                              new_password: '',
                            }));
                          }
                        }
                      }
                      className="rounded-xl h-8 border-gray-200 dark:border-gray-700 dark:bg-slate-800 dark:text-white"
                    />
                  </FormField>
                  <FormField
                    label={t('confirmNewPassword')}
                    icon={KeyRound}
                    required
                    error={formErrors.confirm_password}
                  >
                    <PasswordInput
                      value={passwordFields.confirm_password}
                      onChange={(e) =>
                        {
                          setPasswordFields({
                            ...passwordFields,
                            confirm_password: e.target.value,
                          });
                          if (formErrors.confirm_password) {
                            setFormErrors((prev) => ({
                              ...prev,
                              confirm_password: '',
                            }));
                          }
                        }
                      }
                      className="rounded-xl h-8 border-gray-200 dark:border-gray-700 dark:bg-slate-800 dark:text-white"
                    />
                  </FormField>

                  <button
                    type="submit"
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[12px] font-bold shadow-lg shadow-blue-50 dark:shadow-none transition-colors"
                  >
                    {t('updatePassword')}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleConfirmSubmit} className="space-y-6">
                  <div className="flex gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl">
                    <ShieldAlert
                      size={16}
                      className="text-blue-600 dark:text-blue-400 shrink-0"
                    />
                    <p className="text-[10px] text-blue-700 dark:text-blue-300 font-bold leading-normal">
                      {t('verificationDispatched')}
                    </p>
                  </div>

                  <FormField
                    label={t('verificationCode')}
                    icon={Fingerprint}
                    required
                    error={formErrors.verification_code}
                  >
                    <input
                      className="w-full h-11 text-center text-lg font-bold border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 text-slate-800 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                    />
                  </FormField>
                  {user?.is_2fa_enabled && (
                    <FormField
                      label={t('twoFactorCode')}
                      icon={Fingerprint}
                      required
                      error={formErrors.totp_code}
                    >
                      <input
                        className="w-full h-11 text-center text-lg font-bold border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 text-slate-800 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="000000"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value)}
                      />
                    </FormField>
                  )}

                  <button
                    type="submit"
                    className="w-full py-3 bg-blue-600 text-white rounded-xl text-[11px] font-bold uppercase hover:bg-blue-700 transition-colors"
                  >
                    {t('confirmIdentity')}
                  </button>
                </form>
              )}
              <div className="my-8 border-t border-gray-200 dark:border-gray-800" />
              <TwoFactorAuthSettings user={user} />
              {/* Ownership transfer is a self-only owner action — not shown when
                  browsing someone else's profile. */}
              {isOwnProfile && (
                <>
                  <div className="my-8 border-t border-gray-200 dark:border-gray-800" />
                  <OwnershipTransferSettings currentUser={me} />
                </>
              )}
            </div>
          </section>

          {/* Metadata Detail Card */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-800 shadow-sm space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={14} className="text-gray-400 dark:text-gray-500" />
              <h3 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                {t('systemAudit')}
              </h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400 dark:text-gray-500 font-bold tracking-tighter uppercase">
                  {t('lastLogin')}
                </span>
                <span className="font-bold text-slate-700 dark:text-slate-300">
                  {new Date(user.last_login).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400 dark:text-gray-500 font-bold tracking-tighter uppercase">
                  {t('syncDate')}
                </span>
                <span className="font-bold text-slate-700 dark:text-slate-300">
                  {new Date(user.updated_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="h-px bg-gray-100 dark:bg-gray-800 w-full my-2" />
            <div className="flex items-center gap-2 text-[10px] text-gray-300 dark:text-gray-600 italic">
              <Fingerprint size={12} /> {t('verifiedNodeActive')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ProfilePage;
